import { useState, useEffect } from "react";
import api from "../../services/api";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import "./InventoryReport.css";

export default function InventoryReport() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterStock, setFilterStock] = useState("all"); // all, low, out, normal

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const response = await api.get("/reports/inventory");

      setReportData(response.data);

      Swal.fire({
        icon: "success",
        title: "Report Generated",
        text: "The inventory report was generated successfully",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error generating report:", error);
      Swal.fire("Error", "Could not generate the inventory report", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGenerateReport();
  }, []);

  const handleExportExcel = () => {
    if (!reportData) return;

    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ["INVENTORY REPORT"],
      ["Report Date:", reportData.fecha_consulta],
      [],
      ["SUMMARY"],
      ["Total Products", reportData.resumen.total_productos],
      ["Active Products", reportData.resumen.productos_activos],
      ["Inventory Value (Cost)", `$${reportData.resumen.valor_inventario_costo.toFixed(2)}`],
      ["Inventory Value (Retail)", `$${reportData.resumen.valor_inventario_venta.toFixed(2)}`],
      ["Potential Profit", `$${reportData.resumen.ganancia_potencial.toFixed(2)}`],
      ["Low Stock Products", reportData.resumen.productos_stock_bajo],
      ["Out of Stock Products", reportData.resumen.productos_agotados],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, wsSummary, "Summary");

    const detailData = reportData.detalle.map((item) => ({
      Code: item.codigo,
      Name: item.nombre,
      Stock: item.stock,
      "Min Stock": item.stock_minimo,
      "Purchase Price": item.precio_compra,
      "Sale Price": item.precio_venta,
      "Cost Value": item.valor_costo,
      "Retail Value": item.valor_venta,
      "Potential Profit": item.ganancia_potencial,
      Status: item.estado,
    }));
    const wsDetail = XLSX.utils.json_to_sheet(detailData);
    XLSX.utils.book_append_sheet(workbook, wsDetail, "Inventory");

    const fileName = `Inventory_Report_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    Swal.fire({
      icon: "success",
      title: "Excel Downloaded",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const getFilteredProducts = () => {
    if (!reportData) return [];

    switch (filterStock) {
      case "bajo":
        return reportData.detalle.filter((p) => p.estado === "Bajo" || p.estado === "Crítico");
      case "agotado":
        return reportData.detalle.filter((p) => p.estado === "Agotado");
      case "normal":
        return reportData.detalle.filter((p) => p.estado === "Normal");
      default:
        return reportData.detalle;
    }
  };

  const getStatusLabel = (estado) => {
    const labels = {
      Normal: "Normal",
      Bajo: "Low",
      Crítico: "Critical",
      Agotado: "Out of Stock",
    };
    return labels[estado] || estado;
  };

  const getStatusBadge = (estado) => {
    const badges = {
      Normal: "badge-success",
      Bajo: "badge-warning",
      Crítico: "badge-danger",
      Agotado: "badge-dark",
    };
    return badges[estado] || "badge-secondary";
  };

  const getChartData = () => {
    if (!reportData) return [];
    return reportData.detalle
      .sort((a, b) => b.valor_venta - a.valor_venta)
      .slice(0, 10)
      .map((p) => ({
        nombre: p.nombre.substring(0, 20),
        valor: p.valor_venta,
      }));
  };

  return (
    <div className="inventory-report-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">📦 Inventory Report</h2>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={handleGenerateReport} disabled={loading}>
              {loading ? "⏳ Updating..." : "🔄 Refresh"}
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading && (
            <div className="text-center mt-4">
              <div className="spinner"></div>
              <p className="mt-2">Generating inventory report...</p>
            </div>
          )}

          {reportData && !loading && (
            <div className="report-results">
              <div className="export-section">
                <button className="btn btn-success" onClick={handleExportExcel}>
                  📊 Export to Excel
                </button>
                <span className="text-muted ml-3">
                  Last updated: {new Date(reportData.fecha_consulta).toLocaleString("en-US")}
                </span>
              </div>

              <div className="summary-section">
                <h3 className="section-title">📊 Inventory Summary</h3>
                <div className="kpi-cards">
                  <div className="kpi-card kpi-primary">
                    <div className="kpi-icon">📦</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Products</div>
                      <div className="kpi-value">{reportData.resumen.total_productos}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-info">
                    <div className="kpi-icon">💵</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Cost Value</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.valor_inventario_costo)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-success">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Retail Value</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.valor_inventario_venta)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-warning">
                    <div className="kpi-icon">📈</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Potential Profit</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.ganancia_potencial)}</div>
                    </div>
                  </div>
                </div>

                <div className="alerts-section">
                  <div className="alert alert-warning">
                    ⚠️ <strong>{reportData.resumen.productos_stock_bajo}</strong> products with low or critical stock
                  </div>
                  {reportData.resumen.productos_agotados > 0 && (
                    <div className="alert alert-danger">
                      🚨 <strong>{reportData.resumen.productos_agotados}</strong> products out of stock
                    </div>
                  )}
                </div>
              </div>

              <div className="chart-section">
                <h3 className="section-title">📈 Top 10 Products by Inventory Value</h3>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={getChartData()} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => `$${value.toFixed(0)}`} />
                      <YAxis dataKey="nombre" type="category" width={150} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="valor" fill="#8884d8" name="Retail Value" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="filters-section">
                <h3 className="section-title">🔍 Filter by Status</h3>
                <div className="filter-buttons">
                  <button
                    className={`btn ${filterStock === "all" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setFilterStock("all")}
                  >
                    All ({reportData.detalle.length})
                  </button>
                  <button
                    className={`btn ${filterStock === "normal" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setFilterStock("normal")}
                  >
                    Normal ({reportData.detalle.filter((p) => p.estado === "Normal").length})
                  </button>
                  <button
                    className={`btn ${filterStock === "bajo" ? "btn-warning" : "btn-outline-warning"}`}
                    onClick={() => setFilterStock("bajo")}
                  >
                    Low/Critical ({reportData.resumen.productos_stock_bajo})
                  </button>
                  <button
                    className={`btn ${filterStock === "agotado" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => setFilterStock("agotado")}
                  >
                    Out of Stock ({reportData.resumen.productos_agotados})
                  </button>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="section-title">📋 Product Detail</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Stock</th>
                        <th>Min Stock</th>
                        <th>Cost</th>
                        <th>Price</th>
                        <th>Cost Value</th>
                        <th>Retail Value</th>
                        <th>Pot. Profit</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredProducts().map((item, index) => (
                        <tr key={index}>
                          <td>{item.codigo}</td>
                          <td>{item.nombre}</td>
                          <td className="text-center">
                            <strong
                              className={
                                item.stock === 0 ? "text-danger" : item.stock < item.stock_minimo ? "text-warning" : ""
                              }
                            >
                              {item.stock}
                            </strong>
                          </td>
                          <td className="text-center">{item.stock_minimo}</td>
                          <td className="text-right">{formatCurrency(item.precio_compra)}</td>
                          <td className="text-right">{formatCurrency(item.precio_venta)}</td>
                          <td className="text-right">{formatCurrency(item.valor_costo)}</td>
                          <td className="text-right">{formatCurrency(item.valor_venta)}</td>
                          <td className="text-right text-success">{formatCurrency(item.ganancia_potencial)}</td>
                          <td>
                            <span className={`badge ${getStatusBadge(item.estado)}`}>{getStatusLabel(item.estado)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {!reportData && !loading && (
            <div className="no-data">
              <p>📭 Loading inventory...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}